import { useState, useMemo, useEffect, memo } from 'react';
import { Box, Flex, Heading, TableContainer, Tag, TagLabel, Text, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useGetMessageQuery, useGetTracerQuery } from '@/app/services/nucleus.js';
import { isArrayValue } from '@/utils/helpers.js';
import SingleRowTable from '@/features/common/table/SingleRowTable.jsx';
import { CopyBlock, googlecode } from 'react-code-blocks';
import { formatPythonErrorMessage } from '@/app/services/helper.js';
import TracerCodeBlock from '@/features/troubleshooting/TracerCodeBlock.jsx';
import AlertComponent from '@/features/common/AlertComponent.jsx';
import ActorBadge from '@/features/common/ActorBadge.jsx';
import { useResolvedActor } from '@/hooks/useResolvedActor.js';
import LargePayloadBlock, { isOversizedPayload } from '@/features/troubleshooting/LargePayloadBlock.jsx';

const UUID_LENGTH = 36;

const shortenSystemId = (systemId) => (systemId?.length === UUID_LENGTH ? `${systemId.slice(0, 8)}…${systemId.slice(-4)}` : systemId);

const ActorCell = ({ actor: messageActor }) => {
  const ids = messageActor?.system_ids ?? [];
  // A message on an inbound stream carries only the id the event arrived with, so the lookup
  // falls back to whichever id is there when the message has no nucleus one.
  const chosen = ids.find((entry) => entry.name === 'nucleus') ?? ids[0];
  const { actor, isLoading } = useResolvedActor({ kind: messageActor?.kind, systemName: chosen?.name, systemId: chosen?.system_id });

  return (
    <Flex direction="column" alignItems="flex-start" gap={2}>
      <ActorBadge actor={actor} isLoading={isLoading} />
      {ids.length > 0 && (
        <Flex gap={2} flexWrap="wrap">
          {ids.map((entry) => (
            <Tag key={`${entry.name}-${entry.system_id}`} size="sm" borderRadius={4} colorScheme="gray" variant="subtle">
              <TagLabel>
                {entry.name}:{' '}
                <Box as="span" fontFamily="Fira Code, monospace">
                  {shortenSystemId(entry.system_id)}
                </Box>
              </TagLabel>
            </Tag>
          ))}
        </Flex>
      )}
    </Flex>
  );
};

const MessageDataCell = memo(({ data }) => (
  <div
    style={{
      fontFamily: 'Fira Code, monospace',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'start',
      alignItems: 'center',
    }}
  >
    {isOversizedPayload(data) ? (
      <LargePayloadBlock text={data} label="event message data" />
    ) : (
      <CopyBlock
        codeBlock={true}
        text={data}
        language={'json'}
        showLineNumbers={false}
        startingLineNumber={0}
        customStyle={{
          maxHeight: '700px',
          overflowY: 'scroll',
          width: '100%',
          margin: '0px 0.75rem',
          borderRadius: '0.25rem',
          boxShadow: '1px 2px 3px rgba(0, 0, 0, 0, 0.35)',
          fontSize: '0.75rem',
        }}
        theme={googlecode}
      />
    )}
  </div>
));
MessageDataCell.displayName = 'MessageDataCell';

const ViewLogs = () => {
  const { id, pk } = useParams();
  const [rowColor, setRowColor] = useState('white');
  const { data: tracer, isError, error: tracerError } = useGetTracerQuery({ trace_id: id });
  const [formatMessage, setFormatMessage] = useState(null);

  const log = useMemo(() => {
    if (tracer && tracer[0]?.logs.length > 0) {
      return tracer[0]?.logs.find((l) => l.id === pk);
    }
    return null;
  }, [tracer, pk]);

  const {
    data: message,
    isError: isMessageError,
    error: messageError,
  } = useGetMessageQuery(
    {
      stream_name: log?.stream_name,
      message_id: log?.message_id,
    },
    { skip: !log?.stream_name || !log?.message_id },
  );

  useEffect(() => {
    if (message?.data) {
      try {
        const data = JSON.parse(message.data);
        if (data) {
          setFormatMessage(JSON.stringify(data, null, 2));
        }
      } catch (error) {
        setFormatMessage(message.data);
      }
    }
  }, [message?.data]);

  const errorMessage = useMemo(() => formatPythonErrorMessage(log?.error_message), [log?.error_message]);

  useEffect(() => {
    if (log) {
      if (log.error_message) {
        setRowColor('red.50');
      } else if (log.handled) {
        setRowColor('green.100');
      } else {
        setRowColor('white');
      }
    }
  }, [log]);

  return (
    <Box data-testid="pact-tracer-logs">
      <Heading as="h4" size="md" fontWeight="bold" color="brand.900">
        Tracer Logs
      </Heading>

      {isArrayValue(tracer) && tracer[0].logs.length > 0 && (
        <SingleRowTable
          my={10}
          bgColor={rowColor}
          headers={['Stream', 'Message ID', 'Started', 'Completed']}
          data={[
            tracer[0]?.logs[0].stream_name,
            tracer[0]?.logs[0].message_id,
            new Date(tracer[0]?.logs[0].started).toLocaleString(),
            new Date(tracer[0]?.logs[0].completed_at).toLocaleString(),
          ]}
        />
      )}

      <TableContainer border={'1px solid'} borderColor="gray.100" borderRadius="md" my={10}>
        {isError ? (
          <AlertComponent
            mb={6}
            status="error"
            title="Error Fetching Event Message"
            description={tracerError ? tracerError?.data?.detail : 'Unable to load tracer data. Please contact an administrator.'}
          />
        ) : (
          <Table variant={'simple'} maxWidth={'100%'}>
            <Thead>
              <Tr>
                <Th textAlign={'left'}>
                  Error Message
                  <Text
                    fontSize={'0.75rem'}
                    color="gray.300"
                    fontWeight="normal"
                    sx={{ fontFamily: 'var(--chakra-fonts-body)', textTransform: 'none', letterSpacing: 'normal' }}
                  >
                    If an error message was recorded. It will be displayed here.
                  </Text>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td>{log?.error_message ? <TracerCodeBlock code={errorMessage} language="py" /> : 'No Error \uD83C\uDF89'}</Td>
              </Tr>
            </Tbody>
          </Table>
        )}
      </TableContainer>

      <TableContainer border={'1px solid'} borderColor="gray.100" borderRadius="md" my={10}>
        {isMessageError ? (
          <AlertComponent
            mb={6}
            status="error"
            title="Error Fetching Event Message"
            description={messageError ? messageError?.data?.detail : 'Please contact an administrator'}
          />
        ) : (
          <Table variant={'simple'} maxWidth={'100%'}>
            <Thead>
              <Tr>
                <Th textAlign={'left'} colSpan={2}>
                  Event Message
                  <Text
                    fontSize={'0.75rem'}
                    color="gray.300"
                    fontWeight="normal"
                    sx={{ fontFamily: 'var(--chakra-fonts-body)', textTransform: 'none', letterSpacing: 'normal' }}
                  >
                    Details from the event message sent in stream
                  </Text>
                </Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody fontSize={'sm'}>
              <Tr>
                <Td>Trace ID</Td>
                <Td>{message?.trace_id}</Td>
              </Tr>
              <Tr>
                <Td>Source</Td>
                <Td>{message?.source}</Td>
              </Tr>
              {message?.actor && (
                <Tr>
                  <Td>Actor</Td>
                  <Td>
                    <ActorCell actor={message.actor} />
                  </Td>
                </Tr>
              )}
              <Tr>
                <Td>Data Type</Td>
                <Td>{message?.data_type}</Td>
              </Tr>
              {message?.sub_data_type && (
                <Tr>
                  <Td>Sub Data Type</Td>
                  <Td>{message?.sub_data_type}</Td>
                </Tr>
              )}
              {message?.merge_system_ids && (
                <Tr>
                  <Td>Merge System IDs</Td>
                  <Td>
                    {message?.merge_system_ids.map((id) => (
                      <div key={id.name}>
                        {id.name}: {id.system_id}
                      </div>
                    ))}
                  </Td>
                </Tr>
              )}
              <Tr>
                <Td>Action</Td>
                <Td>{message?.action}</Td>
              </Tr>
              <Tr>
                <Td>Created At</Td>
                <Td>{new Date(message?.created_at * 1000).toLocaleString()}</Td>
              </Tr>
              <Tr>
                <Td>Data</Td>
                <Td>
                  <MessageDataCell data={formatMessage ?? ''} />
                </Td>
              </Tr>
            </Tbody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default ViewLogs;
